import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { SiteService } from '@app/app-state/site.service';
import { UserService } from '@app/app-state/user.service';
import { GenerateChartDataService } from '@app/backend/generate-chart-data.service';
import { ChartDataInterface, FielderAnalyticsInterface, UtilizationGoalDataInterface, UtilizationGoalParams } from '@app/models/chart-data.model';
import * as Highcharts from 'highcharts/highstock';
import * as charts from '@utilities/highchart-objectify';
import { Subscription } from 'rxjs';
import { ChartDataService } from '@app/backend/chart-data.service';
import * as backend from '@app/backend/utilization';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
	selector: 'app-utilization-table-view',
	templateUrl: './utilization-table-view.component.html',
	styleUrls: ['./utilization-table-view.component.scss'],
	standalone: false
})
export class UtilizationTableViewComponent implements OnInit, OnDestroy {

	public Highcharts: typeof Highcharts = Highcharts;
	public chartOptions = [];
	public loading: boolean;
	public isInputChanged: boolean = false;
	public selectedMatTabIndex: number = 0;
	public siteId: number;

	private subscriptions: Subscription = new Subscription();
	private subdomainId: number;
	public domainId: number;
	private startDate: string;
	private endDate: string;
	private selectedTimeline: string;
	public tableLoading: boolean = false;
	private datasetMap: { [name: string]: number } = {};

	public displayedColumns: string[] = ['project', 'utilization', 'utilizationGoal'];
	public dataSource: MatTableDataSource<UtilizationGoalDataInterface>;
	private fielderAnalytics: ChartDataInterface[] = [];
	private utilizationGoalData: UtilizationGoalDataInterface[] = null;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	constructor(
		private chartDataService: ChartDataService,
		private generateChartDataService: GenerateChartDataService,
		private siteService: SiteService,
		private userService: UserService,
		private snackBar: MatSnackBar,
	) { }

	ngOnInit(): void {
		this.subscriptions.add(this.chartDataService.loading$.subscribe(loading => {
			this.loading = loading;
		}));

		this.subscriptions.add(this.chartDataService.fielderAnalytics$.subscribe((res) => {
			this.triggerLoadData(res);
		}));

		this.subscriptions.add(this.siteService.domainId$.subscribe(domainId => {
			this.domainId = domainId;
			if (this.domainId) {
				this.getReport();
			}
		}));

		this.subscriptions.add(this.siteService.subDomainId$.subscribe(subdomainId => {
			this.subdomainId = subdomainId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.siteId$.subscribe(siteId => {
			this.siteId = siteId;
			this.utilizationGoalData = null;
			this.triggerLoadData(this.fielderAnalytics);
			if (this.siteId) {
				this.fetchUtilizationTableData();
			}
		}));

		this.subscriptions.add(this.siteService.startDate$.subscribe(startDate => {
			this.startDate = startDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.endDate$.subscribe(endDate => {
			this.endDate = endDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.selectedTimeline$.subscribe(selectedTimeline => {
			this.selectedTimeline = selectedTimeline;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe(() => {
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.selectedDrillMachine$.subscribe(() => {
			this.chartDataService.getAllFielderAnalytics();
		}));

		this.subscriptions.add(this.chartDataService.fragmentationData$.subscribe(details => {
			this.fetchUtilizationTableData();
		}));
	}

	private async getReport() {
		this.subscriptions.add(this.chartDataService.getCacheFielderAnalytics().subscribe((res) => {
			this.triggerLoadData(res);
		}))
	}

	private loadData = (chartData: ChartDataInterface[]) => {
		this.fielderAnalytics = chartData;
		const data: FielderAnalyticsInterface = this.generateChartDataService.generateChartData(this.fielderAnalytics);
		for (const key in data?.utilizationByShot) {
			this.chartOptions = charts.generateChartOptions(data?.utilizationByShot[key]);
		}
	}

	private async fetchUtilizationTableData() {
		const utilizationChartData = this.chartOptions[0];

		if (utilizationChartData) {
			let utiliuzationSeriesData = utilizationChartData?.series[0]?.data || [];
			const categories = utilizationChartData.xAxis.categories;
			const datasetIds = categories.map(category => {
				const match = this.fielderAnalytics.find(d => d.datasetName === category);
				return match ? match.datasetId : null;
			});

			categories.forEach((category, index) => {
				this.datasetMap[category] = datasetIds[index];
			});

			if (utilizationChartData) {
				if (!this.utilizationGoalData) {
					const utilizationParmas = { siteId: this.siteId };
					this.tableLoading = true;
					const utilizationChartData = await backend.addUpdateUtilizationGoals(utilizationParmas);
					this.tableLoading = false;
					this.utilizationGoalData = utilizationChartData.data;
				}
				if (this.utilizationGoalData.length === 0) {
					const formattedData = utiliuzationSeriesData.map((value, index) => ({
						project: categories[index],
						utilization: value,
						utilizationGoal: 0,
						datasetId: this.datasetMap[categories[index]],
					}));

					this.dataSource = new MatTableDataSource(formattedData);

					this.dataSource.paginator = this.paginator;
				}
				else {
					this.dataSource = new MatTableDataSource(this.utilizationGoalData);
					this.dataSource.paginator = this.paginator;
				}
			}
		}
	}

	public onInputChange() {
		this.isInputChanged = true;
	}

	public addUtilizationValues() {
		if (this.isInputChanged) {
			const updatedData: UtilizationGoalDataInterface[] = this.dataSource.data;
			const utilizationGoalParams: UtilizationGoalParams = {
				siteId: this.siteId,
				goalArray: updatedData.map(item => {
					return {
						project: item.project,
						utilization: item.utilization,
						utilizationGoal: item.utilizationGoal,
						datasetId: item.datasetId,
					};
				})
			};

			this.updateUtilizationGoals(utilizationGoalParams);
			this.isInputChanged = false;
		}
		else {
			this.snackBar.open('Goals not added/updated', ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000 });
		}
	}

	private async updateUtilizationGoals(utilizationGoalParams: UtilizationGoalParams) {
		try {
			this.tableLoading = true;
			const utilizationChartData = await backend.addUpdateUtilizationGoals(utilizationGoalParams);
			this.tableLoading = false;
			if (utilizationChartData) {
				this.utilizationGoalData = utilizationChartData.data;
			}

			this.snackBar.open(utilizationChartData.message, ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000 });
			const originalFielderAnalyticsData = this.generateChartDataService.originalFielderAnalytics;
			utilizationChartData.data.map((res) => {
				originalFielderAnalyticsData.map((fragmentationData, i) => {
					if (res.datasetId == fragmentationData.datasetId) {
						originalFielderAnalyticsData[i]['utilizationGoal'] = res.utilizationGoal;
					}
				});
			})
			this.chartDataService.fragmentationData$.next(originalFielderAnalyticsData);
		} catch (error) {
			console.error("API Error", error);
		}
	}

	private triggerLoadData(data?: ChartDataInterface[]) {
		const originalFielderAnalytics = data || this.generateChartDataService.originalFielderAnalytics;
		this.fielderAnalytics = this.generateChartDataService.getFilteredData(
			originalFielderAnalytics,
			this.domainId,
			this.subdomainId,
			this.siteId,
			this.startDate,
			this.endDate,
			this.selectedTimeline
		);
		this.loadData(this.fielderAnalytics);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

}
